import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Select from 'react-select';
import { withStyles } from '@material-ui/core/styles';
import {
  Chip,
  NoSsr,
  Paper,
  MenuItem,
  TextField,
  Typography,
  InputLabel,
  FormHelperText,
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import { emphasize } from '@material-ui/core/styles/colorManipulator';

const styles = theme => ({
  input: {
    display: 'flex',
    padding: 0,
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
  },
  chip: {
    margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      0.08,
    ),
  },
  noOptionsMessage: {
    padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    fontSize: 16,
  },
  paper: {
    position: 'absolute',
    zIndex: 1,
    marginTop: theme.spacing.unit,
    left: 0,
    right: 0,
  },
  divider: {
    height: theme.spacing.unit * 2,
  },
  label: {
    fontSize: '0.9em',
    height: '15px',
  },
});

function NoOptionsMessage(properties) {
  return (
    <Typography
      color="textSecondary"
      className={properties.selectProps.classes.noOptionsMessage}
      {...properties.innerProps}
    >
      {properties.children}
    </Typography>
  );
}

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}

inputComponent.propTypes = {
  inputRef: PropTypes.func.isRequired,
};

function Control(properties) {
  return (
    <TextField
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: properties.selectProps.classes.input,
          inputRef: properties.innerRef,
          children: properties.children,
          ...properties.innerProps,
        },
      }}
      {...properties.selectProps.textFieldProps}
    />
  );
}

function Option(properties) {
  return (
    <MenuItem
      buttonRef={properties.innerRef}
      selected={properties.isFocused}
      component="div"
      style={{
        fontWeight: properties.isSelected ? 500 : 400,
      }}
      {...properties.innerProps}
    >
      {properties.children}
    </MenuItem>
  );
}

function Placeholder(properties) {
  return (
    <Typography
      color="textSecondary"
      className={properties.selectProps.classes.placeholder}
      {...properties.innerProps}
    >
      {properties.children}
    </Typography>
  );
}

function SingleValue(properties) {
  return (
    <Typography className={properties.selectProps.classes.singleValue} {...properties.innerProps}>
      {properties.children}
    </Typography>
  );
}

function ValueContainer(properties) {
  return <div className={properties.selectProps.classes.valueContainer}>{properties.children}</div>;
}

function MultiValue(properties) {
  return (
    <Chip
      tabIndex={-1}
      label={properties.children}
      className={classNames(properties.selectProps.classes.chip, {
        [properties.selectProps.classes.chipFocused]: properties.isFocused,
      })}
      onDelete={properties.removeProps.onClick}
      deleteIcon={<CancelIcon {...properties.removeProps} />}
    />
  );
}

function Menu(properties) {
  return (
    <Paper square className={properties.selectProps.classes.paper} {...properties.innerProps}>
      {properties.children}
    </Paper>
  );
}

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

class AutoSuggestionSelect extends PureComponent {
  constructor() {
    super();

    this.state = {
      focused: false,
    };

    this.renderLabel = this.renderLabel.bind(this);
    this.renderPlaceholder = this.renderPlaceholder.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
  }

  handleFocus() {
    this.setState({ focused: true });
  }

  handleBlur() {
    this.setState({ focused: false });
  }

  renderLabel() {
    const { required, label } = this.props;
    return required ? `${label} *` : label;
  }

  renderPlaceholder() {
    const { required, placeholder } = this.props;
    return required ? `${placeholder} *` : placeholder;
  }

  render() {
    const {
      error,
      label,
      value,
      theme,
      classes,
      options,
      onChange,
      required,
      autoFocus,
    } = this.props;

    const { focused } = this.state;

    const selectStyles = {
      input: base => ({
        ...base,
        color: theme.palette.text.primary,
      }),
    };

    return (
      <NoSsr>
        { label &&
          <div className={classes.label}>
            <InputLabel className={classes.label} focused={focused}>{value ? this.renderLabel() : ''}</InputLabel>
          </div>
        }
        <Select
          autoFocus={autoFocus}
          required={required}
          classes={classes}
          styles={selectStyles}
          options={options}
          components={components}
          value={value}
          onChange={onChange}
          placeholder={this.renderPlaceholder()}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
        />
        { error && <FormHelperText error>{error}</FormHelperText> }
      </NoSsr>
    );
  }
}

export default withStyles(styles, { withTheme: true })(AutoSuggestionSelect);


AutoSuggestionSelect.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  autoFocus: PropTypes.bool,
  label: PropTypes.string,
  error: PropTypes.string,
};

AutoSuggestionSelect.defaultProps = {
  placeholder: '',
  required: false,
  autoFocus: false,
  value: null,
  label: '',
  error: '',
};




// WEBPACK FOOTER //
// ./src/components/AutoSuggestionSelect.js